// Antivirus gestito da Sentinel (27/10/2026): livello per l'azienda, cosa fare adesso, stato di ogni PC, «Scansiona ora»,
// registro mensile da far firmare al referente (prova per la misura ACN DE.CM-09).
import React, { useState } from 'react';
import type { GuardAvCommand, GuardAvLevel, GuardDeviceDto, GuardSiteDetailDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { ErrorBanner, Pill, Stat } from '../ui';
import { ask } from '../dialogs';
import { saveBlob } from '../files';

const LEVELS: { id: GuardAvLevel; title: string; text: string }[] = [
  { id: 'spento', title: 'Solo controllo', text: 'Guard guarda e avvisa, ma non cambia niente. Nessuna analisi programmata.' },
  { id: 'standard', title: 'Standard (consigliato)', text: 'Defender sempre acceso e aggiornato ogni 4 ore, blocco di programmi indesiderati e siti pericolosi, 7 regole anti-attacco che non disturbano il lavoro. Analisi rapida ogni giorno, completa ogni settimana.' },
  { id: 'rinforzato', title: 'Rinforzato', text: 'Come standard, più 6 regole su Office, script e ransomware. Può bloccare macro o programmi vecchi: provarlo prima su un PC.' },
];
const CMD: Record<GuardAvCommand, string> = { scan_quick: 'analisi rapida', scan_full: 'analisi completa', update: 'aggiornamento' };

const ageText = (d: number | null | undefined) => d == null ? 'mai' : d === 0 ? 'oggi' : d === 1 ? 'ieri' : `${d} giorni fa`;
const supports = (v: string | null) => { const m = /^(\d+)\.(\d+)\.(\d+)/.exec(v ?? ''); return !!m && (Number(m[1]) > 0 || Number(m[2]) > 1 || (Number(m[2]) === 1 && Number(m[3]) >= 4)); };

/** Stesso calcolo del server (antivirus.ts avSummary). */
function summary(d: GuardDeviceDto) {
  const st = d.state;
  const def = st?.defender;
  const defenderOn = !!def && def.realTime === true && (def.mode ?? '').toLowerCase() === 'normal';
  const others = (st?.antivirus ?? []).filter((a) => a.enabled && !/defender/i.test(a.name));
  const isProtected = others.length > 0 || defenderOn;
  const updated = isProtected && others.every((a) => a.upToDate) && (!defenderOn || (def?.signatureAgeDays ?? 99) <= 2);
  return { st, def, defenderOn, others, isProtected, updated, engine: [...others.map((a) => a.name), ...(defenderOn ? ['Microsoft Defender'] : [])].join(', ') };
}

export function SecurityAntivirus({ ctx, site, onChanged }: { ctx: Ctx; site: GuardSiteDetailDto; onChanged: () => Promise<void> }) {
  const level = site.modules.antivirus ?? 'spento';
  const [choice, setChoice] = useState<GuardAvLevel>(level);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const months = Array.from({ length: 12 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return d.toISOString().slice(0, 7); });
  const [month, setMonth] = useState(months[0]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setError(null); setInfo(null);
    try { await fn(); await onChanged(); } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const saveLevel = () => run(async () => { await ctx.api.put(`/api/guard/sites/${site.id}/antivirus`, { level: choice }); setInfo('Livello salvato: i PC lo ricevono entro 15 minuti.'); });
  const command = (d: GuardDeviceDto, kind: GuardAvCommand) => run(async () => {
    await ctx.api.post(`/api/guard/devices/${d.id}/antivirus/command`, { kind });
    setInfo(`${CMD[kind][0].toUpperCase()}${CMD[kind].slice(1)} chiesta su ${d.name}: parte entro 15 minuti.`);
  });
  const commandAll = (kind: GuardAvCommand) => run(async () => {
    if (kind === 'scan_full' && !(await ask('Avviare l\'analisi completa su tutti i PC? Può rallentarli per un\'ora o più.'))) return;
    const r = await ctx.api.post<{ sent: number; oldGuard: number; otherAv: number }>(`/api/guard/sites/${site.id}/antivirus/command`, { kind });
    setInfo(`${CMD[kind][0].toUpperCase()}${CMD[kind].slice(1)} chiesta su ${r.sent} PC: parte entro 15 minuti.${r.oldGuard ? ` ${r.oldGuard} PC hanno un Guard vecchio e la eseguiranno dopo l'aggiornamento.` : ''}${r.otherAv ? ` ${r.otherAv} PC usano un altro antivirus: si gestiscono dalla sua console.` : ''}`);
  });
  const register = () => run(async () => { saveBlob(await ctx.api.download(`/api/guard/sites/${site.id}/antivirus/register?month=${month}`), `registro-antivirus-${month}.pdf`); });

  const rows = site.deviceList.map((d) => ({ d, s: summary(d), threats: site.events.filter((e) => e.deviceId === d.id && e.kind === 'threat.detected' && !e.ackAt).length }));
  const total = rows.length;
  const protectedN = rows.filter((r) => r.s.isProtected).length;
  const updatedN = rows.filter((r) => r.s.updated).length;
  const fullOk = rows.filter((r) => !r.s.defenderOn ? r.s.others.length > 0 : (r.s.def?.fullScanAgeDays ?? 99) <= 7).length;
  const threatsN = rows.reduce((n, r) => n + r.threats, 0);

  // Cosa fare adesso: una riga per problema, con il pulsante giusto.
  const todo: { text: string; action?: { label: string; run: () => void } }[] = [];
  for (const r of rows) {
    if (!r.d.state) { todo.push({ text: `${r.d.name}: nessun dato da Guard. Controllare che il PC sia acceso e collegato.` }); continue; }
    if (r.threats) todo.push({ text: `${r.d.name}: ${r.threats === 1 ? '1 minaccia da guardare' : `${r.threats} minacce da guardare`} (sotto, tra le ultime analisi e minacce). Dopo averla controllata, segnala l'avviso come visto in «PC e avvisi».` });
    if (!r.s.isProtected) todo.push({ text: `${r.d.name}: NESSUN antivirus attivo. ${level === 'spento' ? 'Scegliere il livello standard qui sopra oppure intervenire sul PC.' : 'Guard prova a riaccenderlo: se resta spento, intervenire sul PC.'}` });
    else if (!r.s.updated) todo.push({ text: `${r.d.name}: firme dei virus vecchie.`, action: r.s.defenderOn && supports(r.d.agentVersion) ? { label: 'Aggiorna', run: () => void command(r.d, 'update') } : undefined });
    if (r.s.defenderOn && (r.s.def?.fullScanAgeDays ?? 99) > 7 && !r.d.avCommand)
      todo.push({ text: `${r.d.name}: nessuna analisi completa da più di 7 giorni.`, action: supports(r.d.agentVersion) ? { label: 'Avvia completa', run: () => void command(r.d, 'scan_full') } : undefined });
    if (!supports(r.d.agentVersion)) todo.push({ text: `${r.d.name}: Guard ${r.d.agentVersion ?? '?'} non sa ancora gestire l'antivirus. Si aggiorna da solo quando pubblichiamo la versione 0.1.4.` });
    if (r.d.state?.avPolicy && !r.d.state.avPolicy.applied) todo.push({ text: `${r.d.name}: ${r.d.state.avPolicy.note ?? 'impostazioni non applicate'}.` });
  }

  const box: React.CSSProperties = { border: '1px solid var(--line)', borderRadius: 10, padding: 14, marginBottom: 14 };
  const recent = site.events.filter((e) => e.kind === 'threat.detected' || e.kind.startsWith('av.')).slice(0, 12);

  return (
    <div className="av-view">
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {info && <div className="note good small" style={{ marginBottom: 10 }}>{info}</div>}

      <div style={box}>
        <h3 className="guard-h" style={{ marginTop: 0 }}>Come funziona</h3>
        <ol className="small" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
          <li>Il motore è <b>Microsoft Defender</b>, già dentro Windows. Se un PC ha un altro antivirus (per esempio CrowdStrike), Sentinel lo controlla e basta.</li>
          <li><b>Guard</b> lo tiene acceso e aggiornato, fa l'analisi rapida ogni giorno e quella completa ogni settimana, e avvisa subito se trova qualcosa.</li>
          <li>Ogni mese scarichi il <b>registro antivirus</b> qui sotto e lo fai firmare al referente: è la prova per la NIS2 (misura ACN DE.CM-09), insieme alla procedura DOC-14 in «Documenti NIS2».</li>
        </ol>
      </div>

      <div style={box}>
        <h3 className="guard-h" style={{ marginTop: 0 }}>Livello per questa azienda</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 10 }}>
          {LEVELS.map((l) => (
            <label key={l.id} style={{ border: `2px solid ${choice === l.id ? 'var(--navy, #1d2142)' : 'var(--line)'}`, borderRadius: 10, padding: 10, cursor: 'pointer' }}>
              <input type="radio" name="av-level" style={{ width: 'auto', margin: '0 6px 0 0' }} checked={choice === l.id} onChange={() => setChoice(l.id)} /><b>{l.title}</b>
              {level === l.id && <Pill tone="good" className="small">attivo</Pill>}
              <div className="muted small" style={{ marginTop: 4 }}>{l.text}</div>
            </label>
          ))}
        </div>
        <button type="button" className="btn dark" style={{ marginTop: 10 }} disabled={busy || choice === level} onClick={() => void saveLevel()}>Salva il livello</button>
      </div>

      <div className="stats" style={{ marginBottom: 14 }}>
        <Stat value={`${protectedN}/${total}`} label="PC con antivirus attivo" />
        <Stat value={`${updatedN}/${total}`} label="PC aggiornati (firme di max 2 giorni)" />
        <Stat value={`${fullOk}/${total}`} label="Analisi completa negli ultimi 7 giorni" />
        <Stat value={threatsN} label="Minacce da guardare" />
      </div>

      <div style={box}>
        <h3 className="guard-h" style={{ marginTop: 0 }}>Da fare adesso</h3>
        {total === 0 ? <div className="muted small">Nessun PC con Guard: installa Guard sui PC dell'azienda.</div>
          : todo.length === 0 ? <div className="small"><Pill tone="good">Tutto in regola</Pill> Niente da fare: scarica il registro a fine mese.</div>
          : <ul className="small" style={{ margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
            {todo.map((t, i) => <li key={i}>{t.text} {t.action && <button type="button" className="btn small" disabled={busy} onClick={t.action.run}>{t.action.label}</button>}</li>)}
          </ul>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        <h3 className="guard-h grow" style={{ margin: 0 }}>PC</h3>
        <button type="button" className="btn small" disabled={busy || !total} onClick={() => void commandAll('update')}>Aggiorna tutti</button>
        <button type="button" className="btn small dark" disabled={busy || !total} onClick={() => void commandAll('scan_full')}>Analisi completa su tutti</button>
      </div>
      {total > 0 && (
        <table className="table" style={{ marginBottom: 14 }}>
          <thead><tr><th>PC</th><th>Antivirus</th><th>Firme</th><th>Analisi rapida</th><th>Analisi completa</th><th className="num">Minacce</th><th>Avvia analisi</th></tr></thead>
          <tbody>
            {rows.map(({ d, s, threats }) => (
              <tr key={d.id}>
                <td><b>{d.label || d.name}</b><div className="muted small">{d.label ? `${d.name} · ` : ''}Guard {d.agentVersion ?? '?'}</div></td>
                <td>{!d.state ? '—' : s.isProtected ? <Pill tone="good">{s.engine}</Pill> : <Pill tone="bad">Nessuno attivo</Pill>}
                  {d.state?.avPolicy?.scanning && <div className="muted small">analisi in corso…</div>}</td>
                <td className="small">{!s.defenderOn ? (s.others.length ? <span className="muted">dalla console di {s.others[0].name}</span> : '—')
                  : s.updated ? `aggiornate (${ageText(s.def?.signatureAgeDays)})` : <span style={{ color: 'var(--bad)' }}>vecchie ({ageText(s.def?.signatureAgeDays)})</span>}</td>
                <td className="small">{s.defenderOn ? ageText(s.def?.quickScanAgeDays) : '—'}</td>
                <td className="small">{s.defenderOn ? <span style={{ color: (s.def?.fullScanAgeDays ?? 99) > 7 ? 'var(--bad)' : undefined }}>{ageText(s.def?.fullScanAgeDays)}</span> : '—'}</td>
                <td className="num">{threats ? <Pill tone="bad">{threats}</Pill> : <span className="muted">0</span>}</td>
                <td>
                  {d.avCommand ? <Pill tone="info">{CMD[d.avCommand]} in arrivo</Pill>
                    : !supports(d.agentVersion) ? <span className="muted small">serve Guard 0.1.4</span>
                    : !s.defenderOn ? <span className="muted small">dalla console</span>
                    : <span style={{ display: 'flex', gap: 4 }}>
                      <button type="button" className="btn small" disabled={busy} onClick={() => void command(d, 'scan_quick')}>Veloce</button>
                      <button type="button" className="btn small" disabled={busy} onClick={() => void command(d, 'scan_full')}>Completa</button>
                    </span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={box}>
        <h3 className="guard-h" style={{ marginTop: 0 }}>Registro antivirus del mese</h3>
        <div className="muted small" style={{ marginBottom: 8 }}>Per ogni PC: giorni protetto e aggiornato, analisi eseguite, minacce e cosa si è fatto. Il referente lo legge, lo firma e lo conserva con i documenti NIS2.</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.map((m) => <option key={m} value={m}>{new Date(`${m}-15`).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}</option>)}
          </select>
          <button type="button" className="btn dark" disabled={busy} onClick={() => void register()}>Scarica il registro (PDF)</button>
        </div>
      </div>

      <h3 className="guard-h">Ultime analisi e minacce</h3>
      {recent.length === 0 ? <div className="muted small">Ancora niente: le prime analisi arrivano dopo aver scelto il livello standard.</div> : (
        <div className="ag-list">
          {recent.map((e) => (
            <div key={e.id} className="ag-row" style={{ cursor: 'default' }}>
              <Pill tone={e.severity === 'critical' || e.severity === 'high' ? 'bad' : e.severity === 'medium' ? 'warn' : 'soft'}>{e.kind === 'threat.detected' ? 'Minaccia' : e.kind === 'av.scan' ? 'Analisi' : 'Antivirus'}</Pill>
              <span className="ag-title"><b>{e.title}</b><div className="muted small">{e.deviceName} · {new Date(e.occurredAt).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}</div></span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
